// 强制使用HTTP协议，防止混合内容错误
const getBaseUrl = () => {
  // 统一走 HTTPS 域名：由 nginx 443 反代到 gunicorn 127.0.0.1:8000。
  // 切勿直连 8000（纯 HTTP 端口 + IP 无证书），否则 https 页面下请求全部 Failed to fetch。
  return 'https://www.yangcongstudy.xin';
};

// 获取当前环境的API基础地址
const BASE_URL = getBaseUrl();

// 导出BASE_URL以便在其他组件中使用
export { BASE_URL };

// 协议处理函数 - 不再强制转换，保持原始协议
const enforceHttp = (url) => {
  // 不再强制转换HTTPS为HTTP，保持原始协议
  return url;
};

console.log('[API_EXTENSION] 当前环境基础地址:', BASE_URL);

// 获取存储的令牌
const getAccessToken = () => localStorage.getItem('accessToken');
const getRefreshToken = () => localStorage.getItem('refreshToken');

// 刷新令牌
const refreshToken = async () => {
  const refresh = getRefreshToken();
  if (!refresh) {
    throw new Error('没有刷新令牌，需要重新登录');
  }

  try {
    const response = await fetch(`${BASE_URL}/api/token/refresh/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh }),
    });

    if (!response.ok) {
      throw new Error('刷新令牌失败');
    }

    const data = await response.json();
    localStorage.setItem('accessToken', data.access);
    return data.access;
  } catch (err) {
    // 刷新失败，清除令牌并抛出错误
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    throw err;
  }
};

// 带认证的fetch请求，添加重试机制
const fetchWithAuth = async (url, options = {}, retry = 2) => {
  // 确保URL以/开头并强制使用HTTP协议
  let fullUrl = url.startsWith('/') ? `${BASE_URL}${url}` : url;
  fullUrl = enforceHttp(fullUrl);
  console.log(`[API_EXTENSION] 请求URL: ${fullUrl} (剩余重试次数: ${retry})`);

  // 设置默认headers
  const headers = { ...options.headers, 'Content-Type': 'application/json' };

  // 添加认证令牌
  const token = getAccessToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    console.log('[API_EXTENSION] 已添加认证令牌');
  } else {
    console.log('[API_EXTENSION] 无认证令牌');
  }

  // 合并选项
  const fetchOptions = { ...options, headers };

  try {
    // 发送请求
    console.log('[API_EXTENSION] 开始发送请求');
    let response = await fetch(fullUrl, fetchOptions);
    console.log('[API_EXTENSION] 请求响应状态:', response.status);

    // 处理401错误（令牌过期）
    if (response.status === 401 && token) {
      console.log('[API_EXTENSION] 令牌过期，尝试刷新');
      try {
        // 尝试刷新令牌
        const newToken = await refreshToken();
        // 更新请求头中的令牌
        fetchOptions.headers['Authorization'] = `Bearer ${newToken}`;
        // 重新发送请求
        console.log('[API_EXTENSION] 刷新令牌后重新发送请求');
        response = await fetch(fullUrl, fetchOptions);
        console.log('[API_EXTENSION] 刷新后请求响应状态:', response.status);
      } catch (refreshError) {
        console.error('[API_EXTENSION] 刷新令牌失败:', refreshError.message);
        // 刷新失败，保持原始响应
      }
    }

    return response;
  } catch (err) {
    console.error(`[API_EXTENSION] 请求失败（剩余重试次数: ${retry}）:`, err.message);
    if (retry > 0) {
      // 重试请求
      console.log('[API_EXTENSION] 开始重试请求');
      return fetchWithAuth(url, options, retry - 1);
    }
    throw err; // 重试耗尽后抛出错误
  }
};

// 词汇扩展专用API方法
const apiExtension = {
  // 带认证的fetch请求
  fetchWithAuth,

  // 获取用户信息
  getUserInfo: async () => {
    const response = await fetchWithAuth('/api/user/me/');

    if (!response.ok) {
      throw new Error('获取用户信息失败');
    }

    return response.json();
  },

  // 获取词汇列表（扩展版本，支持包含扩展信息和例句）
  getVocabulary: async (queryString = '') => {
    const baseUrl = queryString ? `/api/sentences/vocabulary/?${queryString}` : '/api/sentences/vocabulary/';
    
    // 只请求第一页，不处理分页
    const response = await fetchWithAuth(baseUrl);
    
    if (!response.ok) {
      throw new Error('获取词汇列表失败');
    }
    
    const data = await response.json();
    const results = Array.isArray(data) ? data : (data.results || []);
    
    // 转换数据结构，确保前端组件能正确识别扩展词和例句
    return results.map(vocab => {
      // 确保扩展信息格式正确
      const processedVocab = {
        ...vocab,
        // 检查是否有vocabulary_extensions字段（旧版）
        extension_info: vocab.extension_info || '',
        synonyms: vocab.synonyms || '',
        antonyms: vocab.antonyms || '',
        // 处理extensions字段（新版）
        vocabulary_extensions: vocab.vocabulary_extensions || [],
        // 确保example_sentences字段格式正确，并且数组中的元素都是对象
        example_sentences: Array.isArray(vocab.example_sentences) ? 
          vocab.example_sentences.filter(item => item && typeof item === 'object') : []
      };
      
      console.log('[API_EXTENSION] 处理后的词汇数据:', {
        id: processedVocab.id,
        english: processedVocab.english,
        hasExtensions: processedVocab.vocabulary_extensions.length > 0,
        hasExampleSentences: processedVocab.example_sentences.length > 0,
        exampleSentencesCount: processedVocab.example_sentences.length,
        exampleSentencesSample: processedVocab.example_sentences.length > 0 ? 
          { english: processedVocab.example_sentences[0].english || 'n/a', 
            chinese: processedVocab.example_sentences[0].chinese || 'n/a' } : 'n/a'
      });
      
      return processedVocab;
    });
  },

  // 获取词汇分类
  getVocabularyCategories: async () => {
    const response = await fetchWithAuth('/api/sentences/categories/?type=vocabulary');
    
    if (!response.ok) {
      throw new Error('获取词汇分类失败');
    }
    
    const data = await response.json();
    
    // 直接返回分类数据
    return Array.isArray(data) ? data : (data.results || []);
  },

  // 登出
  logout: () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  }
};

export default apiExtension;